// DisplayNameStore — per-device persistence of the last display name that
// actually got the user into a room (入室画面の名前記憶).
//
// **Purpose**: 入室のたびに名前を打ち直さなくて済むよう、入室に成功した表示名を
// 端末に 1 つだけ覚える (部屋ごとではない)。ReJoinScreen が欄の初期値として読む。
// 自動入室はしない (押すのは本人)。
//
// 保存の検証は読み戻し側で行う: 長さ制約 (MIN〜MAX_DISPLAY_NAME_LENGTH) を
// 満たさない値は null 扱い。storage 不可 (プライベートモード・学校端末) でも
// throw しない (probe + in-memory fallback、他の *Store と同じ流儀)。

import { MAX_DISPLAY_NAME_LENGTH, MIN_DISPLAY_NAME_LENGTH } from '@oekaki/protocol';

const KEY = 'oekaki:displayName';

/** Minimal Storage shape this module uses (mirrors AdminEmailStore.ts). */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface DisplayNameStore {
  /** Persist a name that successfully joined. Invalid length → no-op. */
  save(displayName: string): void;
  /** Saved name, or `null` if absent / out of length bounds / storage unusable. */
  get(): string | null;
  /** Remove the saved name. Idempotent. */
  clear(): void;
}

const isValidLength = (s: string): boolean =>
  s.length >= MIN_DISPLAY_NAME_LENGTH && s.length <= MAX_DISPLAY_NAME_LENGTH;

const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = `${KEY}__probe__`;
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

const createMemoryStorage = (): StorageLike => {
  const map = new Map<string, string>();
  return {
    getItem: (k) => map.get(k) ?? null,
    setItem: (k, v) => {
      map.set(k, v);
    },
    removeItem: (k) => {
      map.delete(k);
    },
  };
};

export interface CreateDisplayNameStoreOptions {
  storage?: StorageLike | null;
}

export const createDisplayNameStore = (
  opts: CreateDisplayNameStoreOptions = {},
): DisplayNameStore => {
  const explicit = opts.storage;
  const storage =
    explicit === null
      ? createMemoryStorage()
      : (explicit ?? probeBrowserStorage() ?? createMemoryStorage());

  return {
    save(displayName) {
      const trimmed = displayName.trim();
      if (!isValidLength(trimmed)) return;
      try {
        storage.setItem(KEY, trimmed);
      } catch {
        // Quota / disabled storage. Swallow — 次回入室時に復元されないだけ.
      }
    },
    get() {
      try {
        const v = storage.getItem(KEY)?.trim();
        if (v === undefined || !isValidLength(v)) return null;
        return v;
      } catch {
        return null;
      }
    },
    clear() {
      try {
        storage.removeItem(KEY);
      } catch {
        // ignore
      }
    },
  };
};
